import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
} from '@mui/icons-material';
import axios from 'axios';

// Use the same pattern as other services - API_URL should already include /api
const API_URL = process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com';
const BASE_URL = API_URL.replace('/api', ''); // For image URLs

// Create axios instance with auth token
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const GiftBoxes = () => {
  const [giftBoxes, setGiftBoxes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGiftBox, setEditingGiftBox] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'PKR',
    color: '',
    image: '',
    isActive: true,
    priority: 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchGiftBoxes();
  }, []);

  const fetchGiftBoxes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Fetching gift boxes from:', `${API_URL}/gift-boxes`);
      const response = await api.get('/gift-boxes');
      console.log('Gift boxes response:', response.data);
      if (response.data && response.data.success !== false) {
        setGiftBoxes(response.data.giftBoxes || response.data || []);
      } else {
        setError(response.data?.message || 'Failed to fetch gift boxes');
      }
    } catch (err) {
      console.error('Error fetching gift boxes:', err);
      console.error('Error response:', err.response);
      console.error('Request URL:', err.config?.url);
      console.error('Base URL:', err.config?.baseURL);

      if (err.response?.status === 404) {
        setError(`Route not found (404). Trying to reach: ${API_URL}/gift-boxes. Please ensure the backend server is running on port 5001 and has been restarted.`);
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        setError(`Cannot connect to backend server at ${BASE_URL}. Please ensure the backend is running.`);
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        const errorMessage = err.response?.data?.message || err.message || `Failed to fetch gift boxes. Status: ${err.response?.status || 'Unknown'}`;
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (giftBox = null) => {
    setError(null);
    setSuccess(null);
    if (giftBox) {
      setEditingGiftBox(giftBox);
      setFormData({
        name: giftBox.name || '',
        description: giftBox.description || '',
        price: giftBox.price || '',
        currency: giftBox.currency || 'PKR',
        color: giftBox.color || '',
        image: giftBox.image || '',
        isActive: giftBox.isActive !== undefined ? giftBox.isActive : true,
        priority: giftBox.priority || 0,
      });
      setImagePreview(giftBox.image ? `${BASE_URL}${giftBox.image}` : '');
    } else {
      setEditingGiftBox(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        currency: 'PKR',
        color: '',
        image: '',
        isActive: true,
        priority: 0,
      });
      setImagePreview('');
    }
    setImageFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGiftBox(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: 'PKR',
      color: '',
      image: '',
      isActive: true,
      priority: 0,
    });
    setImageFile(null);
    setImagePreview('');
    setError(null);
    setSuccess(null);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Compress image before setting it
      try {
        const compressedFile = await compressImage(file);
        setImageFile(compressedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Image compression error:', error);
        // If compression fails, use original file
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Image compression function
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;

          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = (height * MAX_WIDTH) / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = (width * MAX_HEIGHT) / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            file.type,
            0.85 // 85% quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('price', formData.price);
      formDataToSend.append('currency', formData.currency);
      formDataToSend.append('color', formData.color || '');
      formDataToSend.append('isActive', formData.isActive);
      formDataToSend.append('priority', formData.priority);

      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (formData.image && editingGiftBox) {
        // Only send existing image URL if editing and no new file
        formDataToSend.append('image', formData.image);
      }

      console.log('Submitting gift box:', {
        editing: !!editingGiftBox,
        id: editingGiftBox?._id,
        hasImageFile: !!imageFile,
        hasImageUrl: !!formData.image
      });

      let response;
      if (editingGiftBox) {
        response = await api.put(`/gift-boxes/${editingGiftBox._id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('Gift box updated successfully');
      } else {
        response = await api.post('/gift-boxes', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('Gift box created successfully');
      }

      console.log('Gift box saved successfully:', response.data);

      setTimeout(() => {
        handleCloseDialog();
        fetchGiftBoxes();
      }, 1000);
    } catch (err) {
      console.error('Error saving gift box:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message ||
        err.message ||
        'Failed to save gift box. Please check if the backend server is running.';
      setError(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gift box?')) {
      try {
        await api.delete(`/gift-boxes/${id}`);
        setSuccess('Gift box deleted successfully');
        fetchGiftBoxes();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete gift box');
      }
    }
  };

  if (isLoading && giftBoxes.length === 0 && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          sx={{
            color: 'black',
            fontWeight: 700,
            pb: 2,
            borderBottom: '2px solid #ff2852',
          }}
        >
          Gift Boxes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#ff2852',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#5A189A',
            },
          }}
        >
          Add Gift Box
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={fetchGiftBoxes}>
              Retry
            </Button>
          }
        >
          {error}
          {error.includes('Route not found') && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please ensure the backend server is running on {BASE_URL}
            </Typography>
          )}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!isLoading && giftBoxes.length === 0 && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No gift boxes found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click "Add Gift Box" to create your first gift box
          </Typography>
        </Paper>
      )}

      {giftBoxes.length > 0 && (
        <Paper sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: '#fafafa',
                }}>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>
                    Image
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>
                    Name
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>
                    Color
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>
                    Price
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>
                    Priority
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                    textAlign: 'center',
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {giftBoxes.map((giftBox) => (
                  <TableRow
                    key={giftBox._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(123, 44, 191, 0.05)',
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        component="img"
                        src={giftBox.image ? `${BASE_URL}${giftBox.image}` : '/placeholder.jpg'}
                        alt={giftBox.name}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {giftBox.name}
                      </Typography>
                      {giftBox.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                          {giftBox.description.length > 50
                            ? `${giftBox.description.substring(0, 50)}...`
                            : giftBox.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {giftBox.color || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ color: '#ff2852', fontWeight: 700 }}>
                        {giftBox.currency} {giftBox.price?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {giftBox.priority || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={giftBox.isActive ? 'Active' : 'Inactive'}
                        color={giftBox.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{
                          backgroundColor: giftBox.isActive ? '#4caf50' : '#9e9e9e',
                          color: '#ffffff',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(giftBox)}
                          sx={{
                            color: '#ff2852',
                            '&:hover': {
                              backgroundColor: 'rgba(123, 44, 191, 0.1)',
                            },
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(giftBox._id)}
                          sx={{
                            color: '#d32f2f',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{
            backgroundColor: '#FFFFFF',
            color: '#ff2852',
            fontWeight: 700,
          }}>
            {editingGiftBox ? 'Edit Gift Box' : 'Add Gift Box'}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Price"
                  type="number"
                  fullWidth
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
                <TextField
                  label="Currency"
                  select
                  fullWidth
                  SelectProps={{ native: true }}
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                </TextField>
              </Box>
              <TextField
                label="Color"
                fullWidth
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                placeholder="e.g., Dark Brown, Maroon"
              />
              <TextField
                label="Priority"
                type="number"
                fullWidth
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                helperText="Higher priority displays first"
              />
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Image
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ marginBottom: 8 }}
                />
                {imagePreview && (
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      mt: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              sx={{
                backgroundColor: '#ff2852',
                color: '#000000',
                '&:hover': {
                  backgroundColor: '#5A189A',
                },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default GiftBoxes;
